import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { callFunction } from "./callFunction";

// "How was it?" reviews. The day after an event the backend asks the people who
// came to rate it from 1 to 5, and records each answer at
// eventReviews/{eventId}_{uid} (server-written). Admin operators may list those
// by eventId. Hosts get nothing there: rules cannot hide fields, and every doc
// names who wrote it.
//
// Hosts read eventReviewSummaries/{eventId} instead, also server-written: the
// counts always, and the average, spread and comments (no names) only once 3 or
// more guests have rated. Nothing here writes to either collection.
const REVIEWS = "eventReviews";
const SUMMARIES = "eventReviewSummaries";

class EventReviewApi {
  static collectionName = REVIEWS;

  /**
   * Live map of userId -> { id, eventId, userId, audience, status, askedVia,
   * rating, comments, ... } for one event. Admin operators only; the eventId
   * filter is what lets the rules prove the list. Returns the unsubscribe.
   */
  static subscribeByEvent(eventId, onData, onError) {
    const q = query(collection(db, REVIEWS), where("eventId", "==", eventId));
    return onSnapshot(
      q,
      (snapshot) => {
        const byUser = {};
        snapshot.docs.forEach((d) => {
          const data = d.data();
          if (data?.userId) byUser[data.userId] = { id: d.id, ...data };
        });
        onData(byUser);
      },
      onError
    );
  }

  /** Live eventReviewSummaries/{eventId} data, or null while there is none. */
  static subscribeSummary(eventId, onData, onError) {
    return onSnapshot(
      doc(db, SUMMARIES, eventId),
      (snapshot) => onData(snapshot.exists() ? snapshot.data() : null),
      onError
    );
  }

  /**
   * Take one comment out of the host's summary, or put it back. Admin operators
   * only. The server recomputes the summary even after comments have closed, and
   * the eventReviews subscription above picks up the change.
   */
  static setCommentHidden({ eventId, uid, key, hiddenFromHost }) {
    return callFunction(
      "adminSetReviewComment",
      { eventId, uid, key, hiddenFromHost: hiddenFromHost === true },
      { timeout: 60000 }
    );
  }
}

export default EventReviewApi;
